// Keeps the hazard markers layer on the page's map: added when the map loads,
// re-added after every base-map style switch (which drops custom sources,
// layers and images), removed when the Hazards switch (Maps sheet → More) is off,
// and never added for a signed-out visitor — the /vector proxy would only answer 401.
//
// One per page, like `window.map`: every shared map dialog on the page acquires
// the same controller through the HazardsMapHost its Overlays tab mounts (the
// live locator renders two dialogs), and the layer goes away when the last one
// releases it.
//
// Collaborators are injected so the decisions are unit-tested with a fake map
// and registry; `sharedHazardLayer()` wires the real ones.

import emitter from '../bus';
import { addBundledHazardImage, MissingImageMap } from './hazardMissingImage';
import {
  HAZARD_LAYER_CATEGORY,
  HAZARD_LAYER_ID,
  HAZARD_LAYER_Z,
  HAZARD_SOURCE_ID,
  hazardIdFromFeature,
  hazardLayerSpec,
  hazardSourceSpec,
  hazardTileUrl,
} from './hazardLayerSpec';
import { readHazardsVisible, writeHazardsVisible } from './hazardVisibility';

/**
 * Emitted on the bus whenever the switch changes, so every Hazards switch on the page (one
 * per map dialog's More view) and the store's mirror agree.
 */
export const HAZARDS_VISIBILITY_EVENT = 'hazards:visibility-changed';

export interface HazardLayerMap extends MissingImageMap {
  on(type: string, layerOrListener: any, listener?: any): void;
  off(type: string, layerOrListener: any, listener?: any): void;
  once?(type: string, listener: any): void;
  getSource(id: string): any;
  getLayer(id: string): any;
  getCanvas(): { style: { cursor: string } };
}

export interface HazardLayerRegistry {
  addSource(id: string, spec: any, replace?: boolean): void;
  addLayer(layer: any, category: any, opts?: { z?: number }): void;
  removeLayer(id: string, removeOrphanSource?: boolean): void;
}

export interface EventBus {
  on(event: string, handler: (...args: any[]) => void): void;
  off(event: string, handler: (...args: any[]) => void): void;
  emit(event: string, payload?: any): void;
}

export interface HazardLayerDeps {
  getMap: () => HazardLayerMap | null | undefined;
  getRegistry: () => HazardLayerRegistry | null | undefined;
  isSignedIn: () => boolean;
  origin: () => string;
  bus: EventBus;
  readVisible: () => boolean;
  writeVisible: (enabled: boolean) => void;
  /**
   * A style image the map is missing: adds the bundled icon when it is a hazard
   * marker name (a style without the HiiKER sprite), ignores anything else.
   */
  addMissingImage: (map: HazardLayerMap, id: unknown) => void;
  now: () => number;
}

export type HazardClickHandler = (hazardId: string) => void;
export type HazardClickFilter = (event: any) => boolean;

export class HazardLayerController {
  private enabled: boolean;
  private holders = 0;
  private version = 0;
  private boundMap: HazardLayerMap | null = null;
  private clickHandler: HazardClickHandler | null = null;
  private clickFilter: HazardClickFilter | null = null;
  private retryScheduled = false;
  /** The page's own cursor (the planner's crosshair), put back when the pointer leaves a marker. */
  private cursorBeforeHover = '';

  constructor(private readonly deps: HazardLayerDeps) {
    this.enabled = deps.readVisible();
  }

  /* ---------- lifecycle ---------- */

  /** A map dialog's HazardsMapHost mounted. The first one starts listening and adds the layer. */
  acquire(): void {
    this.holders += 1;
    if (this.holders > 1) return;
    this.deps.bus.on('map-loaded', this.sync);
    this.deps.bus.on('map-style-loaded', this.sync);
    // The map may well have loaded before this host was created.
    this.sync();
  }

  /** A HazardsMapHost unmounted. The last one takes the layer and its listeners away. */
  release(): void {
    if (this.holders === 0) return;
    this.holders -= 1;
    if (this.holders > 0) return;
    this.deps.bus.off('map-loaded', this.sync);
    this.deps.bus.off('map-style-loaded', this.sync);
    this.unbind();
    this.removeLayer();
  }

  /* ---------- state ---------- */

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Whether markers can show at all for this visitor (signed in). */
  isAvailable(): boolean {
    return this.deps.isSignedIn();
  }

  setEnabled(enabled: boolean): void {
    const next = !!enabled;
    if (next === this.enabled) return;
    this.enabled = next;
    this.deps.writeVisible(next);
    this.deps.bus.emit(HAZARDS_VISIBILITY_EVENT, next);
    this.sync();
  }

  /** Who opens a hazard when its marker is clicked (the details panel). */
  setClickHandler(handler: HazardClickHandler | null): void {
    this.clickHandler = handler;
  }

  /** Return true to ignore a marker click, e.g. while the planner is drawing a route. */
  setClickFilter(filter: HazardClickFilter | null): void {
    this.clickFilter = filter;
  }

  /** The tile URL version; bumped by refresh(). */
  tileVersion(): number {
    return this.version;
  }

  /**
   * Fetch the markers again — after a hazard turned out to have left the map —
   * by bumping the tiles' `?t=` cache-buster.
   */
  refresh(): void {
    this.version = Math.max(this.deps.now(), this.version + 1);
    const map = this.deps.getMap();
    const source = map?.getSource(HAZARD_SOURCE_ID);
    if (source && typeof source.setTiles === 'function') {
      try {
        source.setTiles([hazardTileUrl(this.deps.origin(), this.version)]);
      } catch (error) {
        console.warn('[trail_hazards] could not refresh the markers', error);
      }
    }
  }

  /* ---------- map ---------- */

  /** Brings the map in line with the switch: layer on, or layer off. Idempotent. */
  readonly sync = (): void => {
    if (this.holders === 0) return;
    const map = this.deps.getMap();
    const registry = this.deps.getRegistry();
    if (!map || !registry) return;

    if (!this.enabled || !this.deps.isSignedIn()) {
      this.removeLayer();
      return;
    }

    try {
      registry.addSource(HAZARD_SOURCE_ID, hazardSourceSpec(this.deps.origin(), this.version));
      registry.addLayer(hazardLayerSpec(), HAZARD_LAYER_CATEGORY, { z: HAZARD_LAYER_Z });
      this.bind(map);
    } catch (error) {
      // Most often "Style is not done loading". A style switch retries through
      // map-style-loaded; a map that had already fired `load` before the host
      // mounted gets one retry when it next goes idle.
      console.warn('[trail_hazards] could not add the hazard layer yet', error);
      this.scheduleRetry(map);
    }
  };

  private scheduleRetry(map: HazardLayerMap): void {
    if (this.retryScheduled || typeof map.once !== 'function') return;
    this.retryScheduled = true;
    map.once('idle', () => {
      this.retryScheduled = false;
      this.sync();
    });
  }

  private removeLayer(): void {
    const map = this.deps.getMap();
    const registry = this.deps.getRegistry();
    if (!map || !registry) return;
    try {
      if (map.getLayer(HAZARD_LAYER_ID)) registry.removeLayer(HAZARD_LAYER_ID, true);
    } catch (error) {
      console.warn('[trail_hazards] could not remove the hazard layer', error);
    }
  }

  private readonly onClick = (event: any): void => {
    if (this.clickFilter?.(event)) return;
    const hazardId = hazardIdFromFeature(event?.features?.[0]);
    if (hazardId && this.clickHandler) this.clickHandler(hazardId);
  };

  private readonly onMouseEnter = (): void => {
    const map = this.boundMap;
    if (!map) return;
    const canvas = map.getCanvas();
    if (canvas.style.cursor !== 'pointer') this.cursorBeforeHover = canvas.style.cursor;
    canvas.style.cursor = 'pointer';
  };

  private readonly onMouseLeave = (): void => {
    const map = this.boundMap;
    if (map) map.getCanvas().style.cursor = this.cursorBeforeHover;
  };

  /**
   * The map's own style load. A page that changes its style without announcing
   * map-style-loaded on the bus (the planner opened with ?mapStyle=, the park
   * map's late setStyle) would otherwise lose the markers until the next switch.
   */
  private readonly onStyleLoad = (): void => {
    this.sync();
  };

  /**
   * A tile named a marker the style's sprite doesn't have (a style without the
   * HiiKER sprite): add the bundled icon. Other layers' images are not ours.
   */
  private readonly onImageMissing = (event: any): void => {
    const map = this.boundMap;
    if (map) this.deps.addMissingImage(map, event?.id);
  };

  // Layer-scoped listeners are delegated by layer id, so they outlive a style
  // switch — bound once per map instance, not once per re-add.
  private bind(map: HazardLayerMap): void {
    if (this.boundMap === map) return;
    this.unbind();
    map.on('click', HAZARD_LAYER_ID, this.onClick);
    map.on('mouseenter', HAZARD_LAYER_ID, this.onMouseEnter);
    map.on('mouseleave', HAZARD_LAYER_ID, this.onMouseLeave);
    map.on('styleimagemissing', this.onImageMissing);
    map.on('style.load', this.onStyleLoad);
    this.boundMap = map;
  }

  private unbind(): void {
    const map = this.boundMap;
    if (!map) return;
    map.off('click', HAZARD_LAYER_ID, this.onClick);
    map.off('mouseenter', HAZARD_LAYER_ID, this.onMouseEnter);
    map.off('mouseleave', HAZARD_LAYER_ID, this.onMouseLeave);
    map.off('styleimagemissing', this.onImageMissing);
    map.off('style.load', this.onStyleLoad);
    map.getCanvas().style.cursor = '';
    this.boundMap = null;
  }
}

let shared: HazardLayerController | null = null;

/** The page's controller, wired to `window.map`, `window.layerRegistry` and the session. */
export function sharedHazardLayer(): HazardLayerController {
  if (!shared) {
    shared = new HazardLayerController({
      getMap: () => (window as any).map,
      getRegistry: () => (window as any).layerRegistry,
      // The backend user the layout writes into every page; null when signed out.
      isSignedIn: () => !!(window as any).user,
      origin: () => window.location.origin,
      bus: emitter as unknown as EventBus,
      readVisible: () => readHazardsVisible(),
      writeVisible: (enabled) => { writeHazardsVisible(enabled); },
      addMissingImage: (map, id) => { addBundledHazardImage(map, id); },
      now: () => Date.now(),
    });
  }
  return shared;
}
